import { useMemo, useRef, useState } from "react"

import moment from "moment"
import { Grid } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import { DndContext } from "components/lib/dnd/DndContext"

import Cell from "./Cell"

type CalendarType = OpenAPI.GET<"/challenges/{id}/calendar">["response"]["calendar"]
type Event = CalendarType["events"][0]

type CalendarWithDates = Omit<CalendarType, "start" | "end"> & {
  start: Date
  end: Date
}

export type EventWithIndex = Omit<Event, "from" | "to"> & {
  from: Date
  to?: Date
  index: number
  daysFromStart?: number
}

// MARK: Date helpers
export const datePlusDays = (date, days) => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + days)
  return newDate
}

export const dateStart = (date) => {
  const newDate = new Date(date)
  newDate.setHours(0, 0, 0, 0)
  return newDate
}

export const dateEnd = (date) => {
  const newDate = new Date(date)
  newDate.setHours(23, 59, 59, 999)
  return newDate
}

export type CellAction = {
  name: string
  onlyVisibleInRange?: boolean
  action: (dayDate: string) => Event
}

type CalendarProps = CalendarWithDates & {
  setEvents: (events: Event[]) => void
  deleteEvent: (eventId: string, eventType: string) => void
  leaveEvent: (eventId: string) => void
  updateEvent: (event: Event) => void
  setEditingEvent: (event: { from: Date; type: string; id: string }) => void
  editable: boolean
  cellActions: Array<CellAction>
}

const Calendar = ({
  events,
  setEvents,
  updateEvent,
  start: startDate,
  end: endDate,
  deleteEvent,
  leaveEvent,
  setEditingEvent,
  editable,
  cellActions,
}: CalendarProps) => {
  const [selectedEvent, setSelectedEvent] = useState(null)
  const moveTimeout = useRef(null)

  // Add index and create date objects from range strings
  const alignedEvents = useMemo<EventWithIndex[]>(() => {
    const withDates = events.map((event) => ({
      ...event,
      from: new Date(event.from),
      to: new Date(event.to ?? event.from),
    }))

    // Events are sorted by their start day first, if they start on the same day, the longest event comes first
    withDates.sort((a, b) => {
      const aDay = new Date(a.from)
      aDay.setHours(0, 0, 0, 0)
      const bDay = new Date(b.from)
      bDay.setHours(0, 0, 0, 0)

      const startDayDiff = aDay.getTime() - bDay.getTime()
      if (startDayDiff !== 0) {
        return startDayDiff
      }

      const aLength = a.to.getTime() - a.from.getTime()
      const bLength = b.to.getTime() - b.from.getTime()
      return bLength - aLength
    })

    // To display events in the correct order when they span multiple days we need to track their index across multiple days
    const usedIndices = {}
    const indexedEvents = withDates.flatMap((event) => {
      const aligned: EventWithIndex = { index: 0, ...event }

      // background events are not indexed
      if (aligned.eventType === "background") {
        return aligned
      }

      // For each day the event spans, we need to track the index
      const fromDay = new Date(aligned.from)
      fromDay.setHours(0, 0, 0, 0)
      const toDay = new Date(aligned.to)
      toDay.setHours(0, 0, 0, 0)
      const eventDays = (toDay.getTime() - fromDay.getTime()) / (1000 * 60 * 60 * 24)

      let indexToUse = 0
      for (let i = 0; i <= eventDays; i++) {
        const currDate = datePlusDays(aligned.from, i)
        const dateString = currDate.toDateString()

        usedIndices[dateString] = usedIndices[dateString] || {}
        // On the first day we decide which index is assigned to the event, this is just the first available index for the day
        // For every subsequent day we just block the index that was used on the first day
        // This works because the events are already time ordered, so the longest events block out all the days they span
        if (i === 0) {
          while (usedIndices[dateString][indexToUse] !== undefined) {
            indexToUse++
          }
          aligned.index = indexToUse
        }
        usedIndices[dateString][indexToUse] = true
      }
      return aligned
    })
    return indexedEvents.sort((a, b) => a.index - b.index)
  }, [events])

  const [start, startOffset] = useMemo(() => {
    let asDate = startDate

    // pad so that first day is a monday
    const startDay = asDate.getDay()
    if (startDay !== 1) {
      asDate = datePlusDays(asDate, 1 - startDay)
    }
    return [asDate, startDay - 1]
  }, [startDate])

  const end = endDate

  const daysRange = useMemo(
    () => 1 + Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)),
    [start, end],
  )

  return (
    <DndContext
      onDragOver={(moveEvent) => {
        // When an event moves between droppables the event date DAY needs to be updated to represent the new droppables date
        // For this we need to grab the day index of the droppable we are moving to, then the event's date needs to be updated to reflect the new droppable's date
        const movingFrom = moveEvent.active?.data?.current?.sortable?.containerId
        const movingTo = moveEvent.over?.data?.current?.sortable?.containerId

        if (moveEvent.active.id === moveEvent?.over?.id) {
          return
        }

        if (moveTimeout.current && Date.now() < moveTimeout.current) {
          return
        }
        if (!movingTo || movingFrom === movingTo) {
          return
        }

        const [_prefixTo, ...movingToData] = movingTo.split("-")
        const movingToIndex = parseInt(movingToData.join("-"), 10)
        const [_prefixFrom, ...movingFromData] = movingFrom.split("-")
        const movingFromIndex = parseInt(movingFromData.join("-"), 10)
        const movingDiff = movingToIndex - movingFromIndex

        if (Number.isNaN(movingToIndex) || movingToIndex < 0 || Number.isNaN(movingFromIndex) || movingDiff === 0) {
          return
        }

        const activeEventId = (moveEvent.active.id as string).split("+")[0]

        setEvents(
          events.map((event) => {
            if (event.id !== activeEventId) {
              return event
            }

            return {
              ...event,
              from: moment(datePlusDays(event.from, movingDiff)).toISOString(),
              to: moment(datePlusDays(event.to || event.from, movingDiff)).toISOString(),
            }
          }),
        )
        moveTimeout.current = Date.now() + 25
      }}
      onDragEnd={(moveEvent, dropInfo) => {
        if (moveEvent?.active?.data?.current?.sortable?.containerId === dropInfo.originalDroppableId) {
          return
        }

        const event = events.find((e) => (moveEvent.active.id as string).startsWith(`${e.id}+`))
        if (!event) {
          return
        }

        updateEvent(event)
      }}
    >
      <Grid celled>
        <Grid.Row columns={6}>
          <Grid.Column>
            <b>Mon</b>
          </Grid.Column>
          <Grid.Column>
            <b>Tue</b>
          </Grid.Column>
          <Grid.Column>
            <b>Wed</b>
          </Grid.Column>
          <Grid.Column>
            <b>Thu</b>
          </Grid.Column>
          <Grid.Column>
            <b>Fri</b>
          </Grid.Column>
          <Grid.Column>
            <b>Sat/Sun</b>
          </Grid.Column>
        </Grid.Row>
        {Array.from({ length: Math.ceil(daysRange / 7) }, (_, weekNum) => {
          return (
            <Grid.Row
              key={`week-${weekNum}`}
              style={{
                minHeight: 100,
              }}
              columns={6}
            >
              {Array.from({ length: 7 }, (_, dayNum) => {
                const dayCounter = weekNum * 7 + dayNum

                // These props are the same for regular and weekend cells
                const sharedProps = {
                  weekNum,
                  totalDays: daysRange - startOffset,
                  deleteEvent,
                  leaveEvent,
                  onSelectEvent: setSelectedEvent,
                  selectedEvent,
                  cellActions,
                  setEditingEvent,
                  editable,
                }
                // Render weekend inside one cell
                if (dayNum === 5) {
                  return (
                    <Grid.Column style={{ padding: 0 }} key={`weekend-${weekNum}`}>
                      <Grid celled="internally" style={{ height: "100%" }}>
                        <Grid.Row columns={2}>
                          {Array.from({ length: 2 }, (_, weekendDayNum) => {
                            const weekendDayCounter = dayCounter + weekendDayNum
                            const weekendDayStart = dateStart(datePlusDays(start, weekendDayCounter))

                            const weekendDayEnd = dateEnd(weekendDayStart)

                            const weekendEvents = alignedEvents.filter(
                              (event) => event.to >= weekendDayStart && event.from <= weekendDayEnd,
                            )

                            return (
                              <Cell
                                key={`weekend-${weekNum}-${weekendDayNum}`}
                                dayDate={weekendDayStart}
                                daysIntoCalendar={weekendDayCounter - startOffset}
                                dayNumOfTheWeek={dayNum + weekendDayNum}
                                events={weekendEvents}
                                {...sharedProps}
                              />
                            )
                          })}
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  )
                }
                if (dayNum === 6) {
                  return null
                }
                const dateDayStart = dateStart(datePlusDays(start, dayCounter))
                const dateDayEnd = dateEnd(dateDayStart)

                const events = alignedEvents.filter((event) => event.to >= dateDayStart && event.from <= dateDayEnd)
                return (
                  <Cell
                    key={`day-${weekNum}-${dayNum}`}
                    dayDate={dateDayStart}
                    daysIntoCalendar={dayCounter - startOffset}
                    dayNumOfTheWeek={dayNum}
                    events={events}
                    {...sharedProps}
                  />
                )
              })}
            </Grid.Row>
          )
        })}
      </Grid>
    </DndContext>
  )
}

export default Calendar
